/**
 * The kind of cleanliness report an imported lab PDF represents.
 *
 * `Environment` — particle-trap (Restschmutz) analysis from a monitored
 * position in the hall, producing an {@link EnvironmentReport}.
 * `Surface` — component / surface cleanliness analysis of a single part,
 * producing a {@link SurfaceReport} from a Jomesa PicEd Cora ASCII export.
 */
export enum ReportType {
	Environment = "environment",
	Surface = "surface",
}

/** Human-facing labels and i18n keys for each report kind. */
export interface ReportTypeMeta {
	/** Stable identifier used in routing / payloads. */
	type: ReportType;
	/** Short label for buttons and cards (already translated by the caller). */
	labelKey: string;
	/** One-line description shown in the chooser (i18n key). */
	descriptionKey: string;
	/** Material Symbols icon name for the chooser card. */
	icon: string;
	/** Kind is not selectable yet — choosers render it greyed out. */
	disabled?: boolean;
}

export const REPORT_TYPE_META: Record<ReportType, ReportTypeMeta> = {
	[ReportType.Environment]: {
		type: ReportType.Environment,
		labelKey: "traps.import.type.environment.label",
		descriptionKey: "traps.import.type.environment.description",
		icon: "location_on",
		// Environment import is parked until the workflow is finalized (#1105).
		disabled: true,
	},
	[ReportType.Surface]: {
		type: ReportType.Surface,
		labelKey: "traps.import.type.surface.label",
		descriptionKey: "traps.import.type.surface.description",
		icon: "layers",
	},
};

/** Ordered list for chooser UIs. */
export const REPORT_TYPE_ORDER: ReportType[] = [ReportType.Environment, ReportType.Surface];
